import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { QrCode, Copy, Check } from "lucide-react";
import { useState } from "react";

export default function HandleShowcase() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText("@philKholynes.instant");
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <section className="py-24 md:py-32 bg-canvas" data-testid="handle-showcase">
      <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-6">
          <div className="label-eyebrow">{t("home.handle.eyebrow")}</div>
          <h2 className="mt-4 font-display font-medium text-forest text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tightest">
            {t("home.handle.title")} <span className="italic font-serif text-forest/80">{t("home.handle.italic")}</span>
          </h2>
          <p className="mt-6 max-w-lg text-ink/70 leading-relaxed">
            Your Instantxfer CashApp handle works across bank, wallet, and merchant. Print it, share it, or drop
            it on a WhatsApp. Merchants scan an EMVCo QR — settlement is instant.
          </p>

          <div className="mt-8 flex items-center gap-3">
            <div className="rounded-2xl border border-forest/15 bg-white px-5 py-3 flex items-center gap-3">
              <span className="font-mono text-lg text-forest">@philKholynes.instant</span>
              <button onClick={copy} className="text-ink/60 hover:text-forest" data-testid="copy-handle">
                {copied ? <Check className="w-4 h-4 text-forest" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>
            <span className="text-sm text-ink/60">{copied ? "Copied" : "Try copying"}</span>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4">
            {[
              { l: "Compatible with", v: "EMVCo QR" },
              { l: "Merchant fee", v: "0% for Instantxfer CashApp" },
              { l: "Chargebacks", v: "Never" },
            ].map((k) => (
              <div key={k.l}>
                <div className="text-[10px] tracking-[0.22em] uppercase text-ink/50">{k.l}</div>
                <div className="mt-1 font-display text-lg text-forest">{k.v}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-6 relative flex justify-center">
          <motion.div
            initial={{ rotate: -6, opacity: 0, y: 30 }}
            whileInView={{ rotate: -2, opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className="relative bg-forest text-canvas rounded-[32px] p-8 w-[320px] shadow-[0_60px_120px_-40px_rgba(4,26,16,0.55)]"
          >
            <div className="flex items-center justify-between">
              <div className="font-mono text-[11px] tracking-[0.24em] uppercase text-lime">
                Instantxfer CashApp · handle
              </div>
              <span className="live-dot" />
            </div>
            <div className="mt-6 aspect-square rounded-2xl bg-canvas p-4 grid place-items-center relative">
              {/* Fake QR made of blocks */}
              <QRArt />
              <div className="absolute w-14 h-14 rounded-2xl bg-forest grid place-items-center">
                <QrCode className="w-6 h-6 text-lime" />
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div
                className="w-11 h-11 rounded-full ring-2 ring-lime/50 shrink-0 grid place-items-center bg-gradient-to-br from-lime via-lime to-[#c8e600] text-forest text-sm font-bold shadow-[0_6px_18px_-6px_rgba(232,255,0,0.6)]"
                aria-label="Phil-Kholynes Chidi"
                title="Phil-Kholynes Chidi"
                data-testid="handle-card-avatar"
              >
                PK
              </div>
              <div className="min-w-0">
                <div className="font-display text-2xl leading-none">@philKholynes.instant</div>
                <div className="text-canvas/60 text-sm mt-1">Phil-Kholynes Chidi · Lagos, NG</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function QRArt() {
  // Deterministic pseudo-QR (purely decorative)
  const size = 17;
  const pattern = [];
  let seed = 42;
  const rand = () => {
    seed = (seed * 9301 + 49297) % 233280;
    return seed / 233280;
  };
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const isCorner =
        (x < 3 && y < 3) || (x > size - 4 && y < 3) || (x < 3 && y > size - 4);
      pattern.push({ x, y, on: isCorner ? true : rand() > 0.5 });
    }
  }
  return (
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full">
      {pattern.map((p, i) =>
        p.on ? <rect key={i} x={p.x} y={p.y} width="1" height="1" fill="#2D0D5E" /> : null
      )}
      {/* Corner markers */}
      {[
        [0, 0],
        [size - 3, 0],
        [0, size - 3],
      ].map(([cx, cy], i) => (
        <g key={i}>
          <rect x={cx} y={cy} width="3" height="3" fill="#2D0D5E" />
          <rect x={cx + 0.5} y={cy + 0.5} width="2" height="2" fill="#FAFAFA" />
          <rect x={cx + 1} y={cy + 1} width="1" height="1" fill="#2D0D5E" />
        </g>
      ))}
    </svg>
  );
}
